import { catalogKindFromRow, localizeCatalogDisplayName } from '../i18n';
import type { TranslateFn } from '../i18n/operationLabels';
import type { AppLanguage } from '../i18n/types';

const NAME_KEYS = ['name', 'addon_name', 'facility_name', 'title', 'label'] as const;

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function pickRowLabel(row: Record<string, unknown>): string {
  for (const k of NAME_KEYS) {
    const v = row[k];
    if (typeof v === 'string' && v.trim()) {
      return v.trim();
    }
  }
  return '';
}

export type FacilityHighlight = {
  label: string;
  iconId: FacilityIconId;
  kind: 'facility' | 'addon';
};

export type FacilityIconId =
  | 'pool'
  | 'gym'
  | 'parking'
  | 'wifi'
  | 'shower'
  | 'cafe'
  | 'childcare'
  | 'pet'
  | 'maintenance'
  | 'locker'
  | 'pilates'
  | 'cycling'
  | 'weights'
  | 'ac'
  | 'kitchen'
  | 'projector'
  | 'whiteboard'
  | 'restroom'
  | 'accessibility'
  | 'security'
  | 'elevator'
  | 'printer'
  | 'tv'
  | 'meeting'
  | 'storage'
  | 'microwave'
  | 'fridge'
  | 'heating'
  | 'desk'
  | 'phone'
  | 'video'
  | 'soundproof'
  | 'snacks'
  | 'it'
  | 'lounge'
  | 'access24'
  | 'sunlight'
  | 'garden'
  | 'water'
  | 'scanner'
  | 'conference'
  | 'books'
  | 'shelter'
  | 'transit'
  | 'reception'
  | 'ev'
  | 'bicycle'
  | 'privstorage'
  | 'default';

type IconRule = { pattern: RegExp; icon: FacilityIconId };

/** First match wins — keep specific patterns above broad ones. */
const FACILITY_ICON_RULES: IconRule[] = [
  { pattern: /protected space|protect space|safe room|מרחב מוגן|מקלט/, icon: 'shelter' },
  { pattern: /bookshelf|bookshelves|מדפי ספר/, icon: 'books' },
  { pattern: /private storage|אחסון פרטי/, icon: 'privstorage' },
  { pattern: /electric car|ev charg|טעינת רכב|עמדת טעינה/, icon: 'ev' },
  { pattern: /bicycle storage|bike storage|אחסון אופניים|חניות אופנ/, icon: 'bicycle' },
  { pattern: /close to public transportation|public transportation|תחבורה ציבורית|קרוב לתחבורה/, icon: 'transit' },
  { pattern: /close to train|near train|train station|rail station|railway|רכבת|קרוב לרכבת|תחנת רכבת/, icon: 'transit' },
  { pattern: /close to bus|near bus|bus stop|אוטובוס|קרוב לאוטובוס|תחנת אוטובוס/, icon: 'transit' },
  { pattern: /snacks|beverage|refreshment|חטיפ|משקא|כיבוד/, icon: 'snacks' },
  { pattern: /soundproof|sound proof|בידוד אקוסטי/, icon: 'soundproof' },
  { pattern: /dedicated it|it support|tech support|תמיכה it|מחשוב/, icon: 'it' },
  { pattern: /coffee machine|מכונת קפה/, icon: 'cafe' },
  { pattern: /coffee|קפה|קפיטריה/, icon: 'cafe' },
  { pattern: /shower facilities|מתקני מקלחות/, icon: 'shower' },
  { pattern: /shower|showers|sauna|מקלח|סאונ/, icon: 'shower' },
  { pattern: /swimming pool|בריכת שחייה/, icon: 'pool' },
  { pattern: /pool|swim|בריכ|שחי/, icon: 'pool' },
  { pattern: /spin|cycl|bike rack|אופנ|ספינ/, icon: 'cycling' },
  { pattern: /pilates|פילאטיס|יוג/, icon: 'pilates' },
  { pattern: /dumbbell|weight|משקול/, icon: 'weights' },
  { pattern: /fitness|gym access|חדר כושר|מרכז כושר/, icon: 'gym' },
  { pattern: /gym|studio|אימון/, icon: 'gym' },
  { pattern: /lockers|locker room|לוקר|מלתח/, icon: 'locker' },
  { pattern: /child|daycare|מעון|ילד/, icon: 'childcare' },
  { pattern: /pet.friendly|pet friendly|ידידותי לחיות/, icon: 'pet' },
  { pattern: /pet|חיית|כלב|חתול/, icon: 'pet' },
  { pattern: /24\/7|24 7|round the clock|גישה 24/, icon: 'access24' },
  { pattern: /high.speed internet|wireless internet|wi.fi|wifi|אינטרנט|wireless/, icon: 'wifi' },
  { pattern: /air conditioning|מיזוג/, icon: 'ac' },
  { pattern: /\bac\b|a\/c/, icon: 'ac' },
  { pattern: /conference room|חדר כנסים/, icon: 'conference' },
  { pattern: /meeting room|חדר ישיבות/, icon: 'meeting' },
  { pattern: /reception|קבלה/, icon: 'reception' },
  { pattern: /video confer|zoom|ועידת וידאו/, icon: 'video' },
  { pattern: /phone booth|תא טלפון/, icon: 'phone' },
  { pattern: /standing desk|שולחן עמידה/, icon: 'desk' },
  { pattern: /natural light|תאורה טבעית/, icon: 'sunlight' },
  { pattern: /wheelchair|handicap accessible|נגיש לנכים|נגישות/, icon: 'accessibility' },
  { pattern: /cctv|security|אבטחה|מצלמ/, icon: 'security' },
  { pattern: /elevator|lift|מעלית/, icon: 'elevator' },
  { pattern: /whiteboard|לוח מחיק|flipchart/, icon: 'whiteboard' },
  { pattern: /projector|מקרן/, icon: 'projector' },
  { pattern: /scanner|סורק/, icon: 'scanner' },
  { pattern: /printer|מדפסת/, icon: 'printer' },
  { pattern: /microwave|מיקרוגל/, icon: 'microwave' },
  { pattern: /fridge|refrigerator|מקרר/, icon: 'fridge' },
  { pattern: /dishwasher|מדיח/, icon: 'kitchen' },
  { pattern: /kitchen|מטבח/, icon: 'kitchen' },
  { pattern: /water cooler|בר מים|vending/, icon: 'water' },
  { pattern: /restroom|bathroom|toilet|שירותים/, icon: 'restroom' },
  { pattern: /break room|waiting area|lounge|לונג|אזור המתנה/, icon: 'lounge' },
  { pattern: /storage shelf|storage shelves|מדפי אחסון/, icon: 'storage' },
  { pattern: /storage/, icon: 'storage' },
  { pattern: /free parking|חניה חינם/, icon: 'parking' },
  { pattern: /paid parking|חניה בתשלום/, icon: 'parking' },
  { pattern: /car park|parking|חניה|חני/, icon: 'parking' },
  { pattern: /television|\btv\b|טלוויזיה/, icon: 'tv' },
  { pattern: /balcony|terrace|מרפסת|טרסה/, icon: 'garden' },
  { pattern: /garden|גינה/, icon: 'garden' },
  { pattern: /heating|חימום/, icon: 'heating' },
  { pattern: /air purifier|מטהר אוויר/, icon: 'ac' },
  { pattern: /maint|תחזוק|repair/, icon: 'maintenance' },
];

function labelMatchesRule(label: string, pattern: RegExp): boolean {
  const trimmed = label.trim();
  if (!trimmed) {
    return false;
  }
  return pattern.test(trimmed.toLowerCase()) || pattern.test(trimmed);
}

/** Map a facility label to a pictogram id for the asset profile row. */
export function facilityAddonIconId(label: string): FacilityIconId {
  if (!label.trim()) {
    return 'default';
  }
  for (const { pattern, icon } of FACILITY_ICON_RULES) {
    if (labelMatchesRule(label, pattern)) {
      return icon;
    }
  }
  return 'default';
}

/** All facilities (with labels) for the profile icon row. */
export function pickFacilityAddonHighlights(
  facilities: unknown[] | undefined,
  _addons: unknown[] | undefined,
  language: AppLanguage,
  _t: TranslateFn,
): FacilityHighlight[] {
  const out: FacilityHighlight[] = [];

  const pushFacility = (row: Record<string, unknown>) => {
    const raw = pickRowLabel(row);
    if (!raw) {
      return;
    }
    const label = localizeCatalogDisplayName(raw, language, catalogKindFromRow(row));
    out.push({
      label,
      iconId: facilityAddonIconId(raw),
      kind: 'facility',
    });
  };

  if (Array.isArray(facilities)) {
    for (const f of facilities) {
      if (isPlainObject(f)) {
        pushFacility(f);
      }
    }
  }
  return out;
}
